/* eslint-disable no-unused-vars */
import { SyncMediaTimelinePosition  } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-engine";
import { CirrusReaderWebSpeechTtsSynthesizer } from "../shared/CirrusReaderWebSpeechTtsSynthesizer";
import { SyncMediaRangeSeekBehavior, NavigationCollectionType, ContentBlockTargetFetchMode } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import type { IEpubMediaOverlaySyncMediaTimelineOptions } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub";
import type { ISyncMediaTtsTimelineBuilderOptions } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-engine";
import type { 
    ILocator, 
    ILocatorData, 
    ISimpleLocatorData 
} from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import type { 
    IReadingSystemEngine, 
    IReaderPublication, 
    ISyncMediaPlayer, 
    ISyncMediaTimeline, 
    IReaderViewAnnotationLayer, 
    ContainerContentBlockType, 
    MediaContentBlockType, 
    TextContentBlockType, 
    IContentLocation, 
    ISyncMediaPlaybackRangeOptions, 
    ISyncMediaTimelineRangeData, 
    IFetchNavigationItemReferencesResult, 
    ISyncMediaEngineEvent, 
    ISyncMediaPlaybackRange, 
    ITtsUtteranceData, 
    IContentBlock, 
    ISyncMediaRangeEngineEvent
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import type { 
    ICirrusReaderNavigationItem, 
    ICirrusTtsServiceProvider, 
    ICirrusReaderSyncMediaRangePlaybackOptions, 
    ICirrusReaderEscapableContentBlockContext, 
    CirrusMediaPlayerPlaybackStateChangedCallback, 
    CirrusMediaPlayerPlaybackRangeRemovedCallback, 
    CirrusVoidCallback, 
    CirrusMediaPlayerEscapabilityContextCallback, 
    CirrusProgressEventCallback, 
    ICirrusReaderSyncMediaOptions, 
    ICirrusSyncMediaPlaybackStateData, 
    ICirrusSyncMediaSegmentData 
} from "../shared/CirrusReaderModel";

export default defineNuxtPlugin({
    name: 'cirrus-reader-media-player',
    enforce: 'pre',
    dependsOn: [
        'cirrus-reader-reading-system',
        'cirrus-reader-publication',
        'cirrus-reader-view'
    ],
    async setup () {
        const { variant } = useAppVariant()
        if (variant.value !== 'reader') return {};
        
        console.info('[CirrusPlugin] CirrusReaderMediaPlayer.setup()');

        const { $cirrusReaderView, $cirrusReaderPublication } = useNuxtApp();
        const cirrusMediaPlayerStore = useCirrusMediaPlayerStore();
        const cirrusReaderUtils = useCirrusReaderUtils();

        let _colibrioReadingSystem: IReadingSystemEngine;
        let _colibrioReaderPublication: IReaderPublication | undefined = undefined;
        let _colibrioSyncMediaPlayer: ISyncMediaPlayer | null = null;
        let _colibrioSyncMediaTimeline: ISyncMediaTimeline | null = null;
        let _syncMediaTtsColibrioAnnotationLayer: IReaderViewAnnotationLayer;
        let _currentActiveNavigationItems: ICirrusReaderNavigationItem[] | undefined;
        let _ttsVoice: SpeechSynthesisVoice | undefined;
        let _ttsSynthesizer: ICirrusTtsServiceProvider | undefined;
        let _cirrusReaderRangePlaybackOptions: ICirrusReaderSyncMediaRangePlaybackOptions;
        let _escapableContentBlockTypes: (ContainerContentBlockType | MediaContentBlockType | TextContentBlockType)[] = [];
        let _escapabilityContext: ICirrusReaderEscapableContentBlockContext[] = [];
        let _mediaPlayerIsPlaying: boolean = false;

        let _mediaPlayerPlaybackStateChangedCallback: CirrusMediaPlayerPlaybackStateChangedCallback | undefined;
        let _mediaPlayerRangePlaybackEndReachedCallback: CirrusMediaPlayerPlaybackStateChangedCallback | undefined;
        let _mediaPlayerRangePlaybackRangeRemovedCallback: CirrusMediaPlayerPlaybackRangeRemovedCallback | undefined;
        let _attachedCallback: CirrusVoidCallback | undefined;
        let _detachedCallback: CirrusVoidCallback | undefined;
        let _escapabilityContextChangedCallback: CirrusMediaPlayerEscapabilityContextCallback | undefined;
    
        let _cirrusReaderSyncMediaOptions: ICirrusReaderSyncMediaOptions | undefined;
        let _timelineCreationProgressCallback: CirrusProgressEventCallback | undefined;


        const initMediaPlayer = (
            cirrusReaderSyncMediaOptions?: ICirrusReaderSyncMediaOptions,
        ) => {
            console.log("CirrusReaderMediaPlayer.initMediaPlayer");
            
            _cirrusReaderSyncMediaOptions = cirrusReaderSyncMediaOptions;
            _colibrioReadingSystem = $cirrusReaderView.colibrioReaderView.getReadingSystemEngine();
            _ttsSynthesizer = _cirrusReaderSyncMediaOptions?.webSpeechTtsSynthesizer;
            _syncMediaTtsColibrioAnnotationLayer = $cirrusReaderView.colibrioReaderView.createAnnotationLayer('syncmedia-tts-highlights');
            _cirrusReaderRangePlaybackOptions = {
                rangeHighlightColor: _cirrusReaderSyncMediaOptions?.rangePlaybackOptions?.rangeHighlightColor || 'yellow',
                rangeHighlightVisible: _cirrusReaderSyncMediaOptions?.rangePlaybackOptions?.rangeHighlightVisible || true,
            }
        }

        const setTtsVoice = (voice: SpeechSynthesisVoice) => {
            _ttsVoice = voice;
        }

        const onEscapabilityContextChanged = (callback: CirrusMediaPlayerEscapabilityContextCallback): void => {
            _escapabilityContextChangedCallback = callback;
        }
        const onPlaybackStateChanged = (callback: CirrusMediaPlayerPlaybackStateChangedCallback): void => {
            _mediaPlayerPlaybackStateChangedCallback = callback;
        }
        const onRangePlaybackRemoved = (callback: CirrusMediaPlayerPlaybackRangeRemovedCallback): void => {
            _mediaPlayerRangePlaybackRangeRemovedCallback = callback;
        }
        const onRangePlaybackEndReached = (callback: CirrusMediaPlayerPlaybackStateChangedCallback): void => {
            _mediaPlayerRangePlaybackEndReachedCallback = callback;
        }
        const onAttached = (callback: CirrusVoidCallback): void => {
            _attachedCallback = callback;
        }
        const onDetached = (callback: CirrusVoidCallback): void => {
            _detachedCallback = callback;
        }

        const activePlaybackRange = ():  ISyncMediaPlaybackRange | null => {
            return _colibrioSyncMediaPlayer?.getPlaybackRange() || null;
        }


        const attach = (): void => {
            if (_colibrioSyncMediaPlayer) {
                $cirrusReaderView.colibrioReaderView.setSyncMediaPlayer(_colibrioSyncMediaPlayer);
                _syncMediaTtsColibrioAnnotationLayer?.setVisible(true);
                cirrusMediaPlayerStore.setPlayerAttached(true);
    
                if (_attachedCallback) {
                    _attachedCallback();
                }
            }
        }
    
        const detach = (): void => {
            if (_colibrioSyncMediaPlayer) {
                $cirrusReaderView.colibrioReaderView.setSyncMediaPlayer(null);
                _syncMediaTtsColibrioAnnotationLayer?.setVisible(false);
                cirrusMediaPlayerStore.setPlayerAttached(false);
    
                if (_detachedCallback) {
                    _detachedCallback();
                }
            }
        }
    
        const toggle = (doAttach: boolean): void  => {
            if (doAttach) {
                attach();
            } else {
                detach();
            }
        }
    
       const play = (): void => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached) {
                _colibrioSyncMediaPlayer.play();
            }
        }
    
        const pause = (): void => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached) {
                _colibrioSyncMediaPlayer.pause();
            }
        };
    
        const playTimelineRange = (timelineRange: ISyncMediaTimelineRangeData, options?: ISyncMediaPlaybackRangeOptions): void => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached) {
                const startPosition = new SyncMediaTimelinePosition(_colibrioSyncMediaPlayer.getTimeline(), timelineRange.start.segmentIndex, timelineRange.start.offsetWithinSegmentMs);
                const endPosition = new SyncMediaTimelinePosition(_colibrioSyncMediaPlayer.getTimeline(), timelineRange.end.segmentIndex, timelineRange.end.offsetWithinSegmentMs);
                options = options ? options: {
                    pauseOnRangeRemoved: false,
                    removeRangeOnEndReached: true,
                    seekBehaviour: SyncMediaRangeSeekBehavior.REMOVE_IF_OUTSIDE_RANGE
                };
                _colibrioSyncMediaPlayer.setPlaybackRange({start: startPosition, end: endPosition}, options);
                play();
            }
        }
    
        const __playLocatorRange = async (locator: ISimpleLocatorData, options?: ISyncMediaPlaybackRangeOptions): Promise<void> => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached) {
                const location: IContentLocation | undefined = await $cirrusReaderPublication?.colibrioReaderPublication.fetchContentLocation(locator);
                if(!location) {
                    console.warn('CirrusReaderMediaPlayer.playLocator: Unable to fetch a valid content location');
                    return;
                }
                const timeline = _colibrioSyncMediaPlayer.getTimeline();
                const startPosition = await timeline.fetchTimelinePosition(location.collapseToStart());
                const endPosition = await timeline.fetchTimelinePosition(location.collapseToEnd());
    
                playTimelineRange({start: startPosition.toJSON(), end: endPosition.toJSON()},options);
            }
        }
    
        const __cancelRangePlayback = (): void => {
            _colibrioSyncMediaPlayer?.removePlaybackRange();
        }
    
        const seekToNextSegment = (): void => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached) {
                _colibrioSyncMediaPlayer.seekToNextSegment(true);
            }
        };
    
        const seekToPreviousSegment = (): void => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached) {
                _colibrioSyncMediaPlayer.seekToPreviousSegment(true);
            }
        };
    
        const seekToApproximateMs = (ms: number): void => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached) {
                _colibrioSyncMediaPlayer.seekToApproximateTimeMs(ms);
            }
        };
    
        const __seekToPlaybackRangeStart = (): void => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached && activePlaybackRange()) {
                _colibrioSyncMediaPlayer.seekToTimelinePosition(activePlaybackRange()!.range.start);
            }
        };
    
        const setVolume = (volume: number): void => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached) {
                _colibrioSyncMediaPlayer.setVolume(volume);
            }
        };
    
        const setPlaybackRate = (rate: number): void => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached) {
                _colibrioSyncMediaPlayer.setPlaybackRate(rate);
            }
        }
    
        const __setTtsVoiceByName = async (voiceName: string | undefined): Promise<void> => {
            const voicesCollection = await _ttsSynthesizer?.fetchVoices();
    
            const voices = voicesCollection?.allVoices;
    
            if (!voices || !voiceName) {
                _ttsVoice = undefined;
                return;
            }
    
            _ttsVoice = voices.find((voice: SpeechSynthesisVoice) => {
                return voice.name.includes(voiceName);
            });
    
            _ttsSynthesizer?.setActiveVoice(_ttsVoice);
        }
    
        const seekToLocatorWithinVisibleRange = async (locator: ILocator | ILocatorData): Promise<void> => {
            const syncMediaPlayer = _colibrioSyncMediaPlayer;
            const visibleRange = $cirrusReaderView.getVisibleRange();
            if (syncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached && visibleRange) {
    
                const timeline = syncMediaPlayer.getTimeline();
                const visibleRangeStartTimelinePosition = await timeline.fetchTimelinePosition(visibleRange.collapseToStart());
                const pos = await timeline.fetchTimelinePosition(locator);
                const contentLocation = await timeline.fetchContentLocation(pos);
    
                if (visibleRange.contains(contentLocation)) {
                    const posAtStartOfSegment = new SyncMediaTimelinePosition(pos.getTimeline(), pos.getSegmentIndex(), 0);
    
                    if (posAtStartOfSegment.isBefore(visibleRangeStartTimelinePosition)) {
                        syncMediaPlayer.seekToTimelinePosition(visibleRangeStartTimelinePosition);
                    } else {
                        syncMediaPlayer.seekToTimelinePosition(posAtStartOfSegment);
                    }
    
                }
            }
        }
    
        const seekToLocator = async (locator: ILocator | ILocatorData): Promise<void> => {
            if (_colibrioSyncMediaPlayer && cirrusMediaPlayerStore.isPlayerAttached) {
                const mediaPlayer = $cirrusReaderView.colibrioReaderView.getSyncMediaPlayer();
                if (mediaPlayer && locator) {
                    const timeline = mediaPlayer.getTimeline();
                    const pos = await timeline.fetchTimelinePosition(locator);
                    // Always seek to beginning of segment by setting offsetMs to 0
                    const newPosition = new SyncMediaTimelinePosition(pos.getTimeline(), pos.getSegmentIndex(), 0);
                    mediaPlayer.seekToTimelinePosition(newPosition);
                }
            }
        }
    
        const setEscapabableContentBlockTypes = (blockTypes: (ContainerContentBlockType | MediaContentBlockType | TextContentBlockType)[]): void => {
            _escapableContentBlockTypes = blockTypes;
        }


        const fetchRangeContentLocationForSegmentIndex = async (index: number) => {
            if (!_colibrioSyncMediaPlayer || !_colibrioSyncMediaPlayer?.getTimeline()){
                return;
            }
            const startPosition = new SyncMediaTimelinePosition(_colibrioSyncMediaPlayer.getTimeline(), index, 0);
            const endPosition = new SyncMediaTimelinePosition(_colibrioSyncMediaPlayer.getTimeline(), index, Number.MAX_SAFE_INTEGER);
            const startContentLocation = await _colibrioSyncMediaPlayer.getTimeline().fetchContentLocation(startPosition);
            const endContentLocation = await _colibrioSyncMediaPlayer.getTimeline().fetchContentLocation(endPosition);
            return startContentLocation.createRangeTo(endContentLocation);
        }

        /*
        *
        * PRIVATE METHODS
        *
        * */

        const _initTtsAnnotationLayer = (): void => {
            // The TTS SyncMedia can use an annotation layer to highlight the current reading position. Let's set up
            // the layer and set the styling options for the layer and the highlight annotations.
            _syncMediaTtsColibrioAnnotationLayer.setLayerOptions({
                layerStyle: {
                    'mix-blend-mode': 'multiply',
                    opacity: '1',
                },
            });
            _syncMediaTtsColibrioAnnotationLayer.setDefaultAnnotationOptions({
                rangeStyle: {
                    'background-color': '#863ca7',
                    'opacity': '0.3',
                },
            });
        }

        const create = async (): Promise<void> => {
            console.log("CirrusReaderMediaPlayer.create");
            cirrusMediaPlayerStore.clearPlaybackState();
            _colibrioReaderPublication = $cirrusReaderView.colibrioReaderView.getReaderPublications()[0];

            if (_colibrioReaderPublication && 
                (cirrusReaderUtils.isEpubReaderPublication(_colibrioReaderPublication) || 
                    cirrusReaderUtils.isPdfReaderPublication(_colibrioReaderPublication))
            ) {
                // Let's see if this is publication has Media Overlays, if so we create a `IEpubMediaOverlaySyncMediaTimeline`
                if (cirrusReaderUtils.isEpubReaderPublication(_colibrioReaderPublication) && _colibrioReaderPublication.isMediaOverlaySyncMediaAvailable()) {
                    console.log("CirrusReaderMediaPlayer.create: Creating a Sync Media Timeline");
                    const moOptions: IEpubMediaOverlaySyncMediaTimelineOptions = {
                        defaultAudioRendererOptions: {
                            enableMediaStreaming: true,
                        },
                    };
                    _colibrioSyncMediaTimeline = await _colibrioReaderPublication.createMediaOverlaySyncMediaTimeline($cirrusReaderView.colibrioReaderView.getReaderDocuments(), moOptions);
                    _colibrioSyncMediaPlayer = _colibrioReadingSystem.createSyncMediaPlayer(_colibrioSyncMediaTimeline, {
                        preloadAheadMs: 30000
                    });

                } else {
                    console.log("CirrusReaderMediaPlayer.create: Creating a TTS Timeline");
                    // This publication has no Media Overlay so let's create a TTS timeline instead.

                    // TODO: these settings should be customer chosen
                    let wordHighlightEnabled: boolean = true;
                    let highlightFirstWordOnSegmentActive: boolean = true;

                    if (!_ttsSynthesizer) {
                        _ttsSynthesizer = new CirrusReaderWebSpeechTtsSynthesizer((utterance: SpeechSynthesisUtterance, _utteranceAdditionalData: ITtsUtteranceData | undefined): void => {
                            if (_ttsVoice) {
                                utterance.lang = _ttsVoice.lang;
                                utterance.voice = _ttsVoice;
                            }
                        });
                    }

                    if (!_ttsSynthesizer.supportsWordBoundaries) {
                        wordHighlightEnabled = false;
                        highlightFirstWordOnSegmentActive = false;
                    }

                    _initTtsAnnotationLayer();

                    const syncMediaTimelineOptions: ISyncMediaTtsTimelineBuilderOptions = {
                        defaultTtsHighlightLayer: _syncMediaTtsColibrioAnnotationLayer,
                        defaultTtsRendererOptions: {
                            highlightFirstWordOnSegmentActive,
                            wordHighlightEnabled,
                            segmentHighlightEnabled: true,
                            wordHighlightAnnotationOptions: {
                                rangeStyle: {
                                    'filter': 'brightness(1)',
                                    'opacity': '0.8',
                                },
                            },
                        },
                        defaultTtsSynthesizer: _ttsSynthesizer,
                    };

                    // Now that we have the highlight layer set up we can create the SyncMedia Timeline.
                    _colibrioSyncMediaTimeline = await _colibrioReaderPublication.createTtsSyncMediaTimeline(
                        $cirrusReaderView.colibrioReaderView.getReaderDocuments(), 
                        syncMediaTimelineOptions, 
                        _timelineCreationProgressCallback
                    );
                    _colibrioSyncMediaPlayer = _colibrioReadingSystem.createSyncMediaPlayer(_colibrioSyncMediaTimeline);

                }

                attach();

                _colibrioSyncMediaPlayer.addEngineEventListener('syncMediaRangeEndReached', _onRangePlaybackEndReached);
                _colibrioSyncMediaPlayer.addEngineEventListener('syncMediaRangeRemoved', _onRangePlaybackRemoved);
                _colibrioSyncMediaPlayer.addEngineEventListener('syncMediaPaused', _onSyncMediaPlayerPaused);
                _colibrioSyncMediaPlayer.addEngineEventListener('syncMediaPlay', _onSyncMediaPlayerPlay);
                _colibrioSyncMediaPlayer.addEngineEventListener('syncMediaSegmentActive', _onSyncMediaSegmentActive);
                _colibrioSyncMediaPlayer.addEngineEventListener('syncMediaSegmentFinished', _onSyncMediaSegmentFinished);

                if (_cirrusReaderSyncMediaOptions?.rate) {
                    setPlaybackRate(_cirrusReaderSyncMediaOptions.rate);
                }

                if (_cirrusReaderSyncMediaOptions?.volume) {
                    setVolume(_cirrusReaderSyncMediaOptions.volume);
                }

            } else {
                console.warn('CirrusReader.syncMediaPlayer_create Cannot create SyncMediaPlayer. No Colibrio ReaderPublication loaded.');
            }
        };

        const _fetchPlaybackStateData = async (ev: ISyncMediaEngineEvent): Promise<ICirrusSyncMediaPlaybackStateData> => {

            if (!_colibrioSyncMediaPlayer) {
                throw new DOMException('ColibrioSyncMediaPlayer instance does not exist');
            }

            const positionMs = _colibrioSyncMediaPlayer.getApproximateElapsedTimeMs() || 0;
            const position = _colibrioSyncMediaPlayer.getTimelinePosition();
            let location: IContentLocation | undefined;
            if (position) {
                location = await _colibrioSyncMediaPlayer.getTimeline()?.fetchContentLocation(position);
            }
            const rate = _colibrioSyncMediaPlayer.getPlaybackRate();
            const volume = _colibrioSyncMediaPlayer.getVolume();
            const isSeeking = _colibrioSyncMediaPlayer.isSeeking();
            const isMuted = _colibrioSyncMediaPlayer.isMuted();
            const isPaused = _colibrioSyncMediaPlayer.isPaused();
            let locator: string | undefined;
            let navigationItems: ICirrusReaderNavigationItem[] = [];
            let navigationItemRefs: IFetchNavigationItemReferencesResult | undefined;
            const segmentData: ICirrusSyncMediaSegmentData | undefined = _colibrioSyncMediaPlayer.getTimelinePosition().getSegment()?.toJSON();
            const segmentIndex: number | undefined = _colibrioSyncMediaPlayer.getTimelinePosition().getSegmentIndex();
            const publicationDurationMs = _colibrioSyncMediaPlayer.getTimeline().getApproximateDurationMs();
            const positionWithinSegment: number | undefined = _colibrioSyncMediaPlayer.getTimelinePosition().getOffsetWithinSegmentMs();
            const playbackRange: ISyncMediaPlaybackRange | undefined = _colibrioSyncMediaPlayer.getPlaybackRange() || undefined;
            const text = segmentIndex !== undefined ?
                (await (await fetchRangeContentLocationForSegmentIndex(segmentIndex))?.fetchContentResolver())?.getTextContent() :
                undefined;

            const segmentTimelineStartPosition: SyncMediaTimelinePosition | undefined = _colibrioSyncMediaTimeline ?
                new SyncMediaTimelinePosition(_colibrioSyncMediaTimeline, segmentIndex, 0) :
                undefined;

            if(segmentData && segmentTimelineStartPosition) {
                segmentData.segmentTimelineStartOffsetMs = segmentTimelineStartPosition.getTimeline().getApproximateOffsetMs(segmentTimelineStartPosition);
            }

            if (!_currentActiveNavigationItems) {
                navigationItemRefs = await location?.fetchNavigationItemReferences({
                    collectionTypes: [NavigationCollectionType.TOC],
                    greedy: false,
                });

                const itemsInRange= navigationItemRefs?.getItemsInRange() || [];

                for (const ref of itemsInRange) {
                    const navItem = ref.getNavigationItem();
                    const locator = (await ref.getReaderDocument()
                        .getReaderPublication()
                        .fetchContentLocation(navItem)).getLocator().toString();
                    navigationItems.push({
                        locator,
                        children: [],
                        collectionType: ref.getNavigationCollection().getType(),
                        title: ref.getNavigationItem().getTextContent(),
                    });

                }

                // Store this value for upcoming calls. The `this._currentActiveNavigationItems` field is "reset" in the
                // `_event_segmentActive` event handler when we have a new segment.
                _currentActiveNavigationItems = navigationItems;
            } else {
                navigationItems = _currentActiveNavigationItems;
            }

            if (location) {
                locator = location?.getLocator().toString();
            }

            const stateData: ICirrusSyncMediaPlaybackStateData = {
                type: ev.type,
                locator: locator,
                position: positionMs,
                positionWithinSegment,
                navigationItems: navigationItems,
                rate: rate,
                volume: volume,
                isSeeking: isSeeking,
                isMuted: isMuted,
                isPaused: isPaused,
                segmentIndex,
                segmentData: segmentData,
                publicationDurationMs: publicationDurationMs,
                playbackRangeData: (playbackRange?.range.start && playbackRange.range.end) ? {
                    id: playbackRange.id,
                    range: {
                        start: playbackRange.range.start.toJSON(),
                        end: playbackRange.range.end.toJSON(),
                    },
                    options: {
                        pauseOnRangeCanceled: playbackRange.options.pauseOnRangeRemoved,
                        removeRangeOnEndReached: playbackRange.options.removeRangeOnEndReached,
                        seekBehaviour: playbackRange.options.seekBehaviour,
                        rangeHighlightVisible: _cirrusReaderRangePlaybackOptions.rangeHighlightVisible,
                        rangeHighlightColor: _cirrusReaderRangePlaybackOptions.rangeHighlightColor,
                    },
                } : undefined,
                text
            };

            return Promise.resolve(stateData);
        }

        const _maybeDispatchPlaybackStateChangedEvent = (_ev: ISyncMediaEngineEvent): void => {
            _fetchPlaybackStateData(_ev).then((state: ICirrusSyncMediaPlaybackStateData) => {
                cirrusMediaPlayerStore.setPlaybackState(state);
                if (_mediaPlayerPlaybackStateChangedCallback) {
                    _mediaPlayerPlaybackStateChangedCallback(state);
                }
            }).catch(console.warn);
        }




        /*
        *
        * EVENT HANDLERS
        *
        * */

        const _onSyncMediaPlayerPaused = (_ev: ISyncMediaEngineEvent): void => {
            _mediaPlayerIsPlaying = false;
            _maybeDispatchPlaybackStateChangedEvent(_ev);
        }

        const _onSyncMediaPlayerPlay = (_ev: ISyncMediaEngineEvent): void => {
            _mediaPlayerIsPlaying = true;
            _maybeDispatchPlaybackStateChangedEvent(_ev);
        }

        const _onSyncMediaSegmentActive = async (ev: ISyncMediaEngineEvent): Promise<void> => {
            const timelinePosition = ev.timelinePosition;
            const contentLocation = await ev.syncMediaPlayer.getTimeline().fetchContentLocation(timelinePosition);

            _escapabilityContext = await _getEscapabilityContextFromContentLocation(contentLocation);

            if(_escapabilityContextChangedCallback) {
                _escapabilityContextChangedCallback(_escapabilityContext);
            }

            _maybeDispatchPlaybackStateChangedEvent(ev);
        }

        const _onSyncMediaSegmentFinished = (ev: ISyncMediaEngineEvent): void => {

            _maybeDispatchPlaybackStateChangedEvent(ev);
        }

        const _getEscapabilityContextFromContentLocation = async (contentLocation: IContentLocation): Promise<ICirrusReaderEscapableContentBlockContext[]> => {

            const targets =  await contentLocation.fetchContentBlockTargets(ContentBlockTargetFetchMode.INTERSECTING);

            if(!targets || targets[0] == null) {
                return [];
            }

            const contentBlock =  targets[0].getContentBlock();

            const __getNearestEscapableContentBlock = async (contentBlock: IContentBlock, context: IContentBlock []): Promise<IContentBlock[]> => {

                if(_escapableContentBlockTypes.includes(contentBlock.getBlockType())) {
                    context.push(contentBlock)
                }

                const parentContentBlock =  contentBlock.getParent();

                if(!parentContentBlock) {
                    return context;
                }

                return __getNearestEscapableContentBlock(parentContentBlock, context);
            }

            const context = await __getNearestEscapableContentBlock(contentBlock, []);

            return context.map((block)=>{
                const blockLocation = block.getContentLocation();
                return {
                    blockId: block.getId(),
                    block,
                    blockRangeLocation: blockLocation.createRangeTo(blockLocation.collapseToEnd())
                }
            }).reverse();

        }

        const _onRangePlaybackRemoved = (ev: ISyncMediaRangeEngineEvent): void => {
            _fetchPlaybackStateData(ev).then((playbackState: ICirrusSyncMediaPlaybackStateData) => {
                cirrusMediaPlayerStore.setPlaybackState(playbackState);

                if (ev.playbackRange && _mediaPlayerRangePlaybackRangeRemovedCallback) {
                    // The just canceled range was not available to the async `_fetchPlaybackStateData` so we need
                    // to set it here where we still have the event data.
                    playbackState.playbackRangeData = {
                        id: ev.playbackRange.id,
                        range: {
                            start: ev.playbackRange.range.start.toJSON(),
                            end: ev.playbackRange.range.end.toJSON(),
                        }
                    }
                    _mediaPlayerRangePlaybackRangeRemovedCallback(playbackState, ev.reason);
                }
            }).catch(console.warn);
        }

        const _onRangePlaybackEndReached = async (ev: ISyncMediaRangeEngineEvent): Promise<void> => {
            const playbackState = await _fetchPlaybackStateData(ev);
            cirrusMediaPlayerStore.setPlaybackState(playbackState);
            if(ev.playbackRange.options.removeRangeOnEndReached) {
                if(_mediaPlayerRangePlaybackRangeRemovedCallback) {

                    // The just canceled range was not available to the async `_fetchPlaybackStateData` so we need
                    // to set it here where we still have the event data.
                    playbackState.playbackRangeData = {
                        id: ev.playbackRange.id,
                        range: {
                            start: ev.playbackRange.range.start.toJSON(),
                            end: ev.playbackRange.range.end.toJSON(),
                        }
                    }
                    _mediaPlayerRangePlaybackRangeRemovedCallback(playbackState);
                }
            }

            if (playbackState && _mediaPlayerRangePlaybackEndReachedCallback) {
                _mediaPlayerRangePlaybackEndReachedCallback(playbackState);
            }
        }


        return {
            provide: {
                cirrusReaderMediaPlayer: {
                    initMediaPlayer,
                    create,
                    seekToLocator,
                    seekToApproximateMs,
                    seekToNextSegment,
                    seekToPreviousSegment,
                    play,
                    onEscapabilityContextChanged,
                    onPlaybackStateChanged,
                    onRangePlaybackRemoved,
                    onRangePlaybackEndReached,
                    onAttached,
                    onDetached,
                    setEscapabableContentBlockTypes,
                    seekToLocatorWithinVisibleRange,
                    toggle, 
                    pause, 
                    get mediaPlayerIsPlaying() {
                        return _mediaPlayerIsPlaying;
                    },
                    get playerIsAttachedToView(){
                        return cirrusMediaPlayerStore.isPlayerAttached;
                    },
                    get colibrioSyncMediaPlayerInstance(){
                        return _colibrioSyncMediaPlayer
                    },
                    setTtsVoice,
                    fetchRangeContentLocationForSegmentIndex
                }
            },
        }
    },
    env: {
      // Set this value to `false` if you don't want the plugin to run when rendering server-only or island components.
      islands: true
    }
})
